import type { TimePersona } from './time-persona'

/**
 * The `/time/off` tab set (PRD §2.9).
 *
 * Same shape as `time-logs-tabs.ts` and for the same reason: which tabs exist is persona-driven,
 * a worker and a manager see disjoint sets, and "which tabs" and "which one do we land on" are
 * pure functions that should be testable without rendering.
 *
 * Wave 3 shipped the two request surfaces. `leaves` and `holidays` are built as labelled SAMPLE
 * surfaces (TT-V11 stub screens, ADR-012: `LeavePoliciesPanel` / `HolidayCalendarsTab`) — read-only,
 * each carrying its own sample-data banner, until a leave-policy / holiday-calendar backend exists.
 */
/**
 * Order matters: the first PERMITTED tab is the landing tab (`resolveTimeOffTab`). With the tabs
 * ungated (see `TimeOffTabDef.key`), "Approvals" leading would land every worker on a manager's
 * queue — a worse default than the denied state it replaced. "My requests" is what the common
 * persona came for; a manager reaches Approvals in one click.
 */
export const TIME_OFF_TABS = ['requests', 'approvals', 'leaves', 'holidays'] as const
export type TimeOffTab = (typeof TIME_OFF_TABS)[number]

export interface TimeOffTabDef {
  value: TimeOffTab
  label: string
  /**
   * The ACL key gating this tab — present ONLY where a real, SEEDED capability backs it.
   *
   * `undefined` means the tab has nothing to protect. Every tab on this screen currently qualifies:
   * the whole surface is fixture-only (`SURFACE_IS_FIXTURE.timeOff`, and it carries its own
   * `FixtureDataBanner`), and none of the keys these tabs used to carry — `time:off:request`,
   * `time:off:approve`, `time:policies:read`, `time:holidays:read` — appear anywhere in acl-svc's
   * seed catalogue (`config/ui-permissions.csv`; grep for `time:off:` returns nothing). They are
   * FE-side provisional declarations, so `checkPermission` returned false for EVERY persona and
   * `permittedTimeOffTabs` returned `[]` — which `resolveTimeOffTab` correctly reports as DENIED,
   * and the shell rendered "You don't have access to this section" to everyone, forever.
   *
   * This is the same defect EW-FIX-64 fixed in the workspace nav; that fix reached the top-level
   * nav but not this inner list (codex-qa T0164). An unseeded key is not a considered "no", and
   * failing closed on one hides a screen from everybody rather than protecting anything.
   *
   * When a real capability lands, give that tab its key back — and the guard will mean something.
   */
  key?: string
  audience: 'worker' | 'manager'
  /** False until the tab's wave lands. */
  built: boolean
}

export const TIME_OFF_TAB_DEFS: readonly TimeOffTabDef[] = [
  { value: 'requests',  label: 'My requests',       audience: 'worker',  built: true },
  { value: 'approvals', label: 'Approvals',         audience: 'manager', built: true },
  { value: 'leaves',    label: 'Leave policies',    audience: 'worker',  built: true },
  { value: 'holidays',  label: 'Holiday calendars', audience: 'worker',  built: true },
] as const

/**
 * The tabs this caller may see.
 *
 * Still fail-closed WHERE A KEY EXISTS: `checkPermission` is false while loading and on error, so a
 * keyed tab stays hidden until its grant genuinely resolves. Tabs with no key render regardless —
 * see `TimeOffTabDef.key` for why that is honest rather than a hole.
 */
export function permittedTimeOffTabs(
  check: (key: string) => boolean,
  persona: Pick<TimePersona, 'manager'>,
): TimeOffTabDef[] {
  return TIME_OFF_TAB_DEFS.filter(
    // Manager content is derived from the seeded persona keys (ADR-013) — never `time:off:approve`,
    // which acl-svc does not seed. A render affordance, not a security control: the server authorises.
    (t) => (t.audience !== 'manager' || persona.manager) && (t.key === undefined || check(t.key)),
  )
}

/** `null` means the caller holds no tabs — DENIED, which the shell must not render as empty. */
export function resolveTimeOffTab(
  requested: string | null | undefined,
  permitted: TimeOffTabDef[],
): TimeOffTab | null {
  if (permitted.length === 0) return null
  const match = permitted.find((t) => t.value === requested)
  return match ? match.value : permitted[0].value
}
